/**
 * ScrapeKing Design System v2 - Progress & Spinner Components
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ═══════════════════════════════════════════════════════════════
// SPINNER
// ═══════════════════════════════════════════════════════════════

const SpinnerWrapper = styled.div<{ $size: 'sm' | 'md' | 'lg' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${props => props.$size === 'sm' && 'width: 16px; height: 16px;'}
  ${props => props.$size === 'md' && 'width: 24px; height: 24px;'}
  ${props => props.$size === 'lg' && 'width: 40px; height: 40px;'}
`;

const SpinnerRing = styled.div<{ $size: 'sm' | 'md' | 'lg'; $variant: 'cyan' | 'white' }>`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: ${props => props.$size === 'sm' ? '2px' : '3px'} solid var(--sk-surface-border);
  border-top-color: ${props => props.$variant === 'cyan' ? 'var(--sk-cyan-500)' : 'var(--sk-text-primary)'};
  animation: ${spin} 0.8s linear infinite;
`;

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'cyan' | 'white';
  className?: string;
}

export const Spinner: FC<SpinnerProps> = ({
  size = 'md',
  variant = 'cyan',
  className,
}) => {
  return (
    <SpinnerWrapper $size={size} className={className}>
      <SpinnerRing $size={size} $variant={variant} />
    </SpinnerWrapper>
  );
};

// ═══════════════════════════════════════════════════════════════
// LINEAR PROGRESS
// ═══════════════════════════════════════════════════════════════

const ProgressWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-2);
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressLabel = styled.span`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  color: var(--sk-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProgressValue = styled.span`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  color: var(--sk-cyan-500);
`;

const ProgressTrack = styled.div<{ $size: 'sm' | 'md' | 'lg' }>`
  width: 100%;
  height: ${props => {
    switch (props.$size) {
      case 'sm': return '4px';
      case 'md': return '8px';
      case 'lg': return '12px';
    }
  }};
  background: var(--sk-bg-tertiary);
  border-radius: var(--sk-radius-full);
  overflow: hidden;
`;

const ProgressFill = styled.div<{
  $value: number;
  $variant: 'cyan' | 'green' | 'amber' | 'red';
  $indeterminate?: boolean;
}>`
  height: 100%;
  width: ${props => props.$indeterminate ? '50%' : `${Math.min(100, Math.max(0, props.$value))}%`};
  border-radius: var(--sk-radius-full);
  transition: width 0.3s ease;
  position: relative;

  ${props => {
    switch (props.$variant) {
      case 'cyan':
        return css`
          background: linear-gradient(90deg, var(--sk-cyan-600), var(--sk-cyan-500));
          box-shadow: 0 0 10px var(--sk-cyan-glow);
        `;
      case 'green':
        return css`
          background: linear-gradient(90deg, var(--sk-green-600), var(--sk-green-500));
          box-shadow: 0 0 10px var(--sk-green-glow);
        `;
      case 'amber':
        return css`
          background: linear-gradient(90deg, var(--sk-amber-600), var(--sk-amber-500));
          box-shadow: 0 0 10px var(--sk-amber-glow);
        `;
      case 'red':
        return css`
          background: linear-gradient(90deg, var(--sk-red-600), var(--sk-red-500));
          box-shadow: 0 0 10px var(--sk-red-glow);
        `;
    }
  }}

  ${props => props.$indeterminate && css`
    animation: ${shimmer} 1.5s ease infinite;
    background-size: 200% 100%;
    background-image: linear-gradient(
      90deg,
      transparent,
      var(--sk-cyan-500),
      transparent
    );
  `}

  /* Shimmer effect */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    background-size: 200% 100%;
    animation: ${shimmer} 2s ease infinite;
  }
`;

interface ProgressProps {
  value?: number;
  label?: string;
  showValue?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'cyan' | 'green' | 'amber' | 'red';
  indeterminate?: boolean;
  className?: string;
}

export const Progress: FC<ProgressProps> = ({
  value = 0,
  label,
  showValue = true,
  size = 'md',
  variant = 'cyan',
  indeterminate = false,
  className,
}) => {
  return (
    <ProgressWrapper className={className}>
      {(label || showValue) && (
        <ProgressHeader>
          {label && <ProgressLabel>{label}</ProgressLabel>}
          {showValue && !indeterminate && <ProgressValue>{Math.round(value)}%</ProgressValue>}
        </ProgressHeader>
      )}
      <ProgressTrack $size={size}>
        <ProgressFill
          $value={value}
          $variant={variant}
          $indeterminate={indeterminate}
        />
      </ProgressTrack>
    </ProgressWrapper>
  );
};

// ═══════════════════════════════════════════════════════════════
// CIRCULAR PROGRESS
// ═══════════════════════════════════════════════════════════════

const CircularWrapper = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CircularSvg = styled.svg`
  transform: rotate(-90deg);
`;

const CircularTrack = styled.circle`
  fill: none;
  stroke: var(--sk-bg-tertiary);
`;

const CircularFill = styled.circle<{ $variant: 'cyan' | 'green' | 'amber' | 'red' }>`
  fill: none;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.3s ease;

  ${props => {
    switch (props.$variant) {
      case 'cyan': return 'stroke: var(--sk-cyan-500);';
      case 'green': return 'stroke: var(--sk-green-500);';
      case 'amber': return 'stroke: var(--sk-amber-500);';
      case 'red': return 'stroke: var(--sk-red-500);';
    }
  }}
`;

const CircularValue = styled.span<{ $size: number }>`
  position: absolute;
  font-family: var(--sk-font-mono);
  font-size: ${props => props.$size / 4}px;
  font-weight: 600;
  color: var(--sk-text-primary);
`;

interface CircularProgressProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  showValue?: boolean;
  variant?: 'cyan' | 'green' | 'amber' | 'red';
  className?: string;
}

export const CircularProgress: FC<CircularProgressProps> = ({
  value,
  size = 80,
  strokeWidth = 6,
  showValue = true,
  variant = 'cyan',
  className,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;

  return (
    <CircularWrapper $size={size} className={className}>
      <CircularSvg width={size} height={size}>
        <CircularTrack
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <CircularFill
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          $variant={variant}
        />
      </CircularSvg>
      {showValue && <CircularValue $size={size}>{Math.round(value)}%</CircularValue>}
    </CircularWrapper>
  );
};

// ═══════════════════════════════════════════════════════════════
// LOADING OVERLAY
// ═══════════════════════════════════════════════════════════════

const OverlayWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sk-space-4);
  background: rgba(0, 0, 0, 0.6);
  z-index: 100;
`;

const LoadingText = styled.span`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
  animation: ${pulse} 1.5s ease infinite;
`;

interface LoadingOverlayProps {
  text?: string;
  className?: string;
}

export const LoadingOverlay: FC<LoadingOverlayProps> = ({
  text = 'Loading...',
  className,
}) => {
  return (
    <OverlayWrapper className={className}>
      <Spinner size="lg" />
      <LoadingText>{text}</LoadingText>
    </OverlayWrapper>
  );
};

export default { Spinner, Progress, CircularProgress, LoadingOverlay };
